import { Form } from 'antd';
import dayjs from 'dayjs';
import isEqual from 'lodash/isEqual';
import moment from 'moment';
import { useCallback, useMemo, useRef, useState } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';

import type { ModelClientFieldValue } from '@app/api/Client';
import {
  useGetClientFieldValuesQuery,
  useLazyGetClientByIdQuery,
  useLazyGetClientFieldValuesQuery,
  useSetClientFieldValuesMutation,
} from '@app/api/Client/api';
import { useLazyGetClientServicesByEmployeeIdQuery } from '@app/api/ClientServices';
import type { ModelRecordProduct } from '@app/api/Products/types';
import {
  useDeleteRecordFromJournalMutation,
  useGetEmployeesByServicesMutation,
  useLazyGetRecordByIdQuery,
} from '@app/api/Records/api';
import {
  invalidateClientSubscriptions,
  useSetRecordSubscriptionsMutation,
} from '@app/api/Subscriptions';
import type { PhoneValidation } from '@app/components/Phone/types';
import {
  getPhoneDigits,
  normalizeRussianLocalPhoneNumber,
} from '@app/components/Phone/utils';
import { useRecordClientSearch } from '@app/components/RecordModal/components/RecordDetails/hooks/useRecordClientSearch';
import {
  PHONE_CLIENT_FAILED_ERROR,
  PHONE_CLIENT_LOADING_ERROR,
  PHONE_CLIENT_RESULT_ERRORS,
  useRecordPhoneClientSearch,
} from '@app/components/RecordModal/components/RecordDetails/hooks/useRecordPhoneClientSearch';
import {
  ClientStatusEnum,
  RecordTypeEnum,
} from '@app/components/RecordModal/components/RecordDetails/types';
import type {
  BranchUserOption,
  ProductSaleDraftItem,
  RecordDetailsForm,
  SelectedEmployee,
  Service,
  ServiceGroup,
  SubscriptionSaleDraftItem,
  TagOption,
  UseRecordDetailsProps,
} from '@app/components/RecordModal/components/RecordDetails/types';
import {
  getBransUserOptions,
  getServicesOptions,
  getTagsOptions,
  rgbToHex,
} from '@app/components/RecordModal/components/RecordDetails/utils';
import {
  buildRecordClientServices,
  getChangedSavedServices,
  getChangedSavedServicesMessage,
  getNotProvidedSubscriptionServices,
  getServiceNames,
  mapRecordServicesToFormServices,
  removeClientSubscriptionServices,
  replaceSavedServices,
  updateRecordWithSavedServices,
} from '@app/components/RecordModal/components/RecordDetails/utils/recordServices';
import {
  RECORD_SUBSCRIPTIONS_NO_RECORD_ERROR,
  buildRecordSubscriptionsPayload,
  getRecordSubscriptionsSaveErrorText,
  getSavedRecordSubscriptions,
  hasRecordSubscriptionsChanges,
  mapRecordSubscriptionsToDraftItems,
  validateRecordSubscriptions,
} from '@app/components/RecordModal/components/RecordDetails/utils/recordSubscriptions';
import type { SavedRecordSubscriptions } from '@app/components/RecordModal/components/RecordDetails/utils/recordSubscriptions';
import { saveBeforeEmployeeChange } from '@app/components/RecordModal/components/RecordDetails/utils/saveBeforeEmployeeChange';
import { getServicesDurationFields } from '@app/components/RecordModal/components/RecordDetails/utils/servicesDuration';
import { useRecordAfterSave } from '@app/components/RecordModal/hooks/useRecordAfterSave';
import {
  closeRecordModal,
  setClientId,
  setClientName,
  setDate,
  setEmployee,
  setHasChanges,
  setIsInitialized,
  setIsLoading,
  setMode,
  setNextTab,
  openRecordTagsWindow,
  setPrepareRecordCreationResponse,
  setRecord,
  setRecordIdForOpen,
  setRecordTab,
  setUnsavedRecordForm,
  setClientAllRecords,
  setWarnings,
  setIsFullyPaid,
  setSavedClient,
} from '@app/components/RecordModal/model/recordModalSlice';
import { selectRecordModal } from '@app/components/RecordModal/model/selectors';
import { MenuTabEnum } from '@app/components/RecordModal/types';
import {
  CALENDAR_PATH,
  FRONT_WARNING,
  RIGHTS_CLIENTS,
  RIGHTS_IS_SHOW_CONTACTS,
  SUCCESS,
  WARNING,
} from '@app/constants';
import {
  useAccessRights,
  useNewUserAccessRights,
  useUserPrint,
} from '@app/hooks';
import { useRecordModalContext } from '@app/providers';
import { useNotification } from '@app/providers/NotificationProvider/NotificationContext';
import { handleCatchError } from '@app/services';
import {
  useLazyCheckRecordTimesQuery,
  useLazyPrepareRecordCreationQuery,
  useUpdateRecordMutation,
} from '@app/services/rtkQuery/Records/api';
import { useLazyGetAllTagsQuery } from '@app/services/rtkQuery/Tags/api';
import { useAppDispatch, useAppSelector } from '@app/store';
import type {
  ClientServiceInterface,
  NEmployees,
  NSmallGlobalEntities,
  NRecords,
  OtherRecordsInterface,
  RecordInterface,
  TBranchUser,
  TEmployeeOptions,
  TTag,
  UpdateRecordDataInterface,
} from '@app/types';
import type { NServices } from '@app/types/Services';
import { createId } from '@app/utils';

import type { Rule, RuleObject } from 'antd/es/form';
import type { FormInstance } from 'antd/lib';

const { useForm, useWatch } = Form;

const mapRecordProductsToDraftItems = (
  products: ModelRecordProduct[] | null | undefined
): ProductSaleDraftItem[] => {
  if (!products) return [];

  return products.map((product) => {
    const price = product.Price ?? 0;
    const quantity = product.Count ?? 0;
    const discountCurrency = product.Discount ?? 0;
    const total = price * quantity;
    const discountPercent = total > 0 ? (discountCurrency / total) * 100 : 0;

    return {
      id: createId(),
      groupId: product.GroupId ?? null,
      warehouseId: product.WarehouseId,
      productId: product.ProductId,
      productName: product.Name ?? null,
      price,
      quantity,
      countAvailable: undefined,
      discountCurrency,
      discountPercent,
      sellerId: product.SellerId ?? null,
      discounts: product.Discounts ?? [],
      totalDiscount: product.TotalDiscount ?? 0,
      paid: product.Paid,
      cost: product.Cost,
      isExcludedFromLoyaltyDiscount: product.IsExcludedFromLoyaltyDiscount,
      // Capture the persisted identity so the original GroupId can be restored
      // if the user replaces the product and then selects it back again.
      originalGroupId: product.GroupId ?? null,
      originalWarehouseId: product.WarehouseId,
      originalProductId: product.ProductId,
    };
  });
};

// GroupId belongs to the originally persisted product, not to the UI row.
// Reuse it only when the currently selected product matches the persisted
// identity (same product in the same warehouse); otherwise the backend must
// treat the row as a new product and we send null.
const getProductPayloadGroupId = (
  item: ProductSaleDraftItem
): string | null => {
  if (!item.originalGroupId) return null;

  const isOriginalProduct =
    item.productId === item.originalProductId &&
    item.warehouseId === item.originalWarehouseId;

  return isOriginalProduct ? item.originalGroupId : null;
};

const buildRecordProductsPayload = (
  items: ProductSaleDraftItem[] | undefined
): ModelRecordProduct[] => {
  if (!items?.length) return [];

  return items
    .filter(
      (
        item
      ): item is ProductSaleDraftItem & {
        warehouseId: number;
        productId: number;
        quantity: number;
      } => Boolean(item.warehouseId && item.productId && item.quantity)
    )
    .map((item) => ({
      WarehouseId: item.warehouseId,
      ProductId: item.productId,
      GroupId: getProductPayloadGroupId(item),
      Name: item.productName ?? null,
      Price: item.price ?? 0,
      Discount: item.discountCurrency ?? 0,
      Count: item.quantity ?? 0,
      SellerId: item.sellerId ?? null,
      Discounts: item.discounts ?? null,
      Cost: item.cost ?? null,
      Paid: item.paid ?? null,
      TotalDiscount: item.totalDiscount ?? 0,
      IsExcludedFromLoyaltyDiscount:
        item.isExcludedFromLoyaltyDiscount ?? false,
    }));
};

const validateRecordProducts = (
  items: ProductSaleDraftItem[] | undefined
): string | null => {
  if (!items?.length) return null;

  for (const item of items) {
    if (!item.warehouseId) return 'Выберите склад для товара';
    if (!item.productId) return 'Выберите товар';

    if (
      !item.quantity ||
      !Number.isInteger(item.quantity) ||
      item.quantity < 1
    ) {
      return 'Укажите корректное количество товара';
    }

    if (
      item.countAvailable !== undefined &&
      item.quantity > item.countAvailable
    ) {
      return `Количество товара "${
        item.productName ?? ''
      }" превышает доступное (${item.countAvailable})`;
    }

    const total = (item.price || 0) * item.quantity;

    if (item.discountCurrency < 0 || item.discountCurrency > total) {
      return 'Скидка товара указана некорректно';
    }
  }

  return null;
};

// Проданный абонемент записан на клиента записи (beauty-crm
// LogicSubscriptions.cs:422, :453), а UpdateRecord его не переносит на
// нового клиента: у такой записи клиента не меняем (issue/11575).
export const CLIENT_SEARCH_SOLD_SUBSCRIPTION_HINT =
  'Нельзя сменить клиента: в записи продан абонемент';

// Клиент, чей номер стоит в поле «Мобильный телефон»: Id, номер и имя на
// момент выбора клиента, загрузки или сохранения записи. isPhoneEmpty — у
// клиента пустой Phone1: UpdateRecord запишет в него номер записи, И1 для
// такого клиента не действует (решение техлида, шаг 2).
interface PhoneClient {
  clientId: number;
  phone: string;
  isPhoneEmpty: boolean;
  nameFirst: string;
  nameLast: string;
}

const getComparablePhone = (phone: string | null | undefined): string =>
  normalizeRussianLocalPhoneNumber(getPhoneDigits(phone ?? ''));

const createPhoneClient = (
  clientId: number,
  phone: string | null | undefined,
  nameFirst: string | null | undefined,
  nameLast: string | null | undefined,
  clientPhone1: string | null | undefined = phone
): PhoneClient => ({
  clientId,
  phone: getComparablePhone(phone),
  isPhoneEmpty: !getComparablePhone(clientPhone1),
  nameFirst: nameFirst ?? '',
  nameLast: nameLast ?? '',
});

const DEFAULT_PHONE_COUNTRY_ID = 1;
const getRecordPhoneCountryId = (
  countryId: number | null | undefined,
  fallbackCountryId = DEFAULT_PHONE_COUNTRY_ID
): number => {
  if (typeof countryId === 'number' && Number.isFinite(countryId)) {
    return countryId > 0 ? countryId : fallbackCountryId;
  }

  return fallbackCountryId;
};

export const useRecordDetails = ({
  confirmUnsavedData,
  confirmWarnings,
}: UseRecordDetailsProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [getClientById] = useLazyGetClientByIdQuery();

  const { afterSave } = useRecordAfterSave();
  const { pathname } = useLocation();
  const [prepareRecordCreation] = useLazyPrepareRecordCreationQuery();
  const [updateRecord] = useUpdateRecordMutation();
  const [setRecordSubscriptions] = useSetRecordSubscriptionsMutation();
  // Абонементы записи после последнего успешного SetRecordSubscriptions —
  // эталон для следующего «Сохранить». Загрузка записи с сервера его сбрасывает.
  const savedSubscriptionsRef = useRef<SavedRecordSubscriptions | null>(null);
  const [getClientFieldValues] = useLazyGetClientFieldValuesQuery();
  const [setClientFieldValues] = useSetClientFieldValuesMutation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [phoneValidation, setPhoneValidation] = useState<PhoneValidation>({
    isValid: false,
    errorMessage: null,
  });
  const [selectedEmployee, setSelectedEmployee] = useState<SelectedEmployee>({
    RecordId: 0,
    EmployeeId: 0,
  });
  const [branchEmployees, setBranchEmployees] = useState<TBranchUser[]>([]);
  const [serviceEmployees, setServiceEmployees] = useState<
    NRecords.ModelEmployeeByService[]
  >([]);
  const [additionalEmployees, setAdditionalEmployees] = useState<
    Array<number | null>
  >([]);
  const [isCommentModal, setIsCommentModal] = useState(false);
  const [branchUsers, setBranchUsers] = useState<TBranchUser[]>([]);
  const [warehouses, setWarehouses] = useState<
    NSmallGlobalEntities.ModelItem[]
  >([]);
  const [savedData, setSavedData] = useState<RecordDetailsForm | null>(null);
  // Строки «Услуг» последнего сохранения или загрузки: от их количества
  // считается предел услуг с абонемента (task/11536).
  const savedServices = useMemo(() => savedData?.services ?? [], [savedData]);
  const formRef = useRef<FormInstance<RecordDetailsForm>>(null);
  const dispatch = useAppDispatch();
  const [tags, setTags] = useState<TTag[]>([]);

  const [serviceOptions, setServiceOptions] = useState<ServiceGroup[]>([]);
  const [clientServices, setClientServices] = useState<
    ClientServiceInterface[]
  >([]);
  const [allEmployeeServices, setAllEmployeeServices] = useState<
    NEmployees.ModelClientService[]
  >([]);
  const [errorModalMessage, setErrorModalMessage] = useState('');

  const isOpenErrorModal = useMemo(
    () => !!errorModalMessage,
    [errorModalMessage]
  );
  const closeErrorModal = () => setErrorModalMessage('');

  const branchUserOptions: BranchUserOption[] = useMemo(
    () => getBransUserOptions(branchUsers),
    [branchUsers]
  );

  const [allTags, setAllTags] = useState<TTag[]>([]);
  const [getAllTags] = useLazyGetAllTagsQuery();
  const [checkRecordTimes] = useLazyCheckRecordTimesQuery();
  const { showNotification } = useNotification();
  const {
    isOpen,
    record,
    employee,
    mode,
    time,
    endTime,
    clientId: currentClientId,
    clientName: currentClientName,
    savedClient,
    savedClientRestoreCount,
    branchId: modalBranchId,
    isLoading,
    date,
    hasChanges,
    isInitialized,
    nextTab,
    recordIdForOpen,
    clientAllRecords,
    isFullyPaid,
    prepareRecordCreationResponse,
  } = useAppSelector(selectRecordModal);
  const { onConfirmRef } = useRecordModalContext();

  const [form] = useForm<RecordDetailsForm>();
  const {
    setFieldValue,
    setFieldsValue,
    resetFields,
    submit,
    getFieldValue,
    getFieldsValue,
    setFields,
    validateFields,
    scrollToField,
    getFieldError,
  } = form;
  const formValues = useWatch([], form);
  const clientIdQueryParam = +(searchParams.get('clientId') ?? 0);
  const tagOptions: TagOption[] = useMemo(() => getTagsOptions(tags), [tags]);
  const [equalEmployeePayoff, setEqualEmployeePayoff] = useState(false);

  const { hasUserRight: rightToViewContacts } = useAccessRights(
    RIGHTS_CLIENTS,
    RIGHTS_IS_SHOW_CONTACTS
  );

  const IMPORTANT_EMPLOYEE_TAG_ID = tags.find(
    (tag) => tag.SystemId === 'TagImportantEmployee'
  )?.Id;

  const selectedImportantEmployeeTag = useMemo(
    () => formValues?.Tags?.find((tag) => tag === IMPORTANT_EMPLOYEE_TAG_ID),
    [formValues, IMPORTANT_EMPLOYEE_TAG_ID]
  );

  const {
    userId,
    database,
    activeBranchId,
    params,
    activeBranchCountryId: activeBranchCountry,
  } = useUserPrint();
  const recordActiveBranchId = modalBranchId ?? activeBranchId;
  const [getRecordById] = useLazyGetRecordByIdQuery();
  const [getEmployeesByServices] = useGetEmployeesByServicesMutation();
  const [deleteRecordFromJournal, { isLoading: isDeleteRecordLoading }] =
    useDeleteRecordFromJournalMutation();
  const recordParams = useMemo(
    () => ({
      ...params,
      ActiveBranchId: recordActiveBranchId ?? params.ActiveBranchId,
    }),
    [params, recordActiveBranchId]
  );

  const {
    clientSearchTerm,
    setClientSearchTerm,
    clientSearchResults,
    clientSearchIsLoading,
    clientSearchIsSuccess,
    isClientSearchTooShort,
    onChangeClientSearchTerm,
    resetClientSearch,
  } = useRecordClientSearch({ recordActiveBranchId, rightToViewContacts });

  const {
    phoneClients,
    isPhoneClientMenuOpen,
    isPhoneClientSearchLoading,
    isPhoneClientSearchSuccess,
    phoneClientSearchError,
    searchedPhone,
    searchPhoneClients,
    resetPhoneClientSearch,
    onPhoneInputFocus,
    onPhoneInputBlur,
    onPhoneCountryListOpenChange,
  } = useRecordPhoneClientSearch({ recordActiveBranchId });
  const phoneClientRef = useRef<PhoneClient | null>(null);

  // Клиент для номера определён: ошибка поиска у поля телефона гаснет.
  const clearPhoneClientError = useCallback(
    () => setFields([{ name: 'Phone', errors: [] }]),
    [setFields]
  );

  // «Уйти без изменений» на вкладке «Детали записи» возвращает клиента записи
  // (restoreSavedClient). Поля клиента и номер возвращаются вместе с ним:
  // иначе ClientId записи ушёл бы с номером другого клиента (И1).
  const lastSavedClientRestoreCountRef = useRef(savedClientRestoreCount);
  const restoreSavedClientFields = useCallback(() => {
    if (savedClientRestoreCount === lastSavedClientRestoreCountRef.current) {
      return;
    }

    lastSavedClientRestoreCountRef.current = savedClientRestoreCount;

    if (!savedData) return;

    const { NameFirst, NameLast, IsPhoneVerified, Phone } = savedData;
    const savedClientId = savedClient?.clientId ?? 0;

    resetPhoneClientSearch();
    phoneClientRef.current = savedClientId
      ? createPhoneClient(savedClientId, Phone?.Phone, NameFirst, NameLast)
      : null;
    setFieldsValue({ NameFirst, NameLast, IsPhoneVerified, Phone });
    clearPhoneClientError();
  }, [
    savedClientRestoreCount,
    savedData,
    savedClient,
    resetPhoneClientSearch,
    setFieldsValue,
    clearPhoneClientError,
  ]);

  // Тот же запрос (и кэш RTK Query), что у useClientImportantFields:
  // значения важных полей сеются в форму ПОСЛЕ фиксации savedData, поэтому
  // эталон для сравнения несохранённых данных берём из этих же данных.
  const { data: clientFieldValues } = useGetClientFieldValuesQuery(
    { ActiveBranchId: params.ActiveBranchId, clientId: currentClientId ?? 0 },
    { skip: !currentClientId || !params.ActiveBranchId }
  );
  const clientFieldBaseline = useMemo(() => {
    const baseline: Record<string, string> = {};
    (clientFieldValues ?? []).forEach(({ FieldId, IsImportant, Value }) => {
      if (IsImportant) baseline[String(FieldId)] = Value ?? '';
    });
    return Object.keys(baseline).length ? baseline : undefined;
  }, [clientFieldValues]);

  const getAllTagsHandler = useCallback(async () => {
    try {
      const response = await getAllTags({
        ActiveBranchId: recordActiveBranchId,
      }).unwrap();

      setAllTags(response as TTag[]);
    } catch (error) {
      showNotification(error, WARNING);
    }
  }, [showNotification, recordActiveBranchId, getAllTags]);

  const { userAccessRights } = useNewUserAccessRights();
  const {
    Records: { IsDelete, IsCreate, IsEdit, IsPayRecord, IsEditPaidRecord },
    Salaries: { EditEmployeePayoff },
  } = userAccessRights;

  const [isOpenDeleteRecordModal, setIsOpenDeleteRecordModal] = useState(false);

  const openDeleteRecordModal = () => setIsOpenDeleteRecordModal(true);
  const closeDeleteRecordModal = () => setIsOpenDeleteRecordModal(false);

  const showDeleteButton = useMemo(
    () => mode === 'edit' && IsDelete && IsEdit && !record?.IsDeleted,
    [IsDelete, IsEdit, mode, record?.IsDeleted]
  );

  const isSaveRecordButtonDisabled = useMemo(() => {
    switch (mode) {
      case 'create':
        return !IsCreate;
      case 'edit':
        return !IsEdit;
      default:
        return true;
    }
  }, [IsCreate, IsEdit, mode]);

  const employeeId = useWatch('Employee', form);

  const recordId = useMemo(() => record?.Id || null, [record?.Id]);

  // Тип записи считает бэк (us/11144): у существующей записи он только
  // показывается и в UpdateRecord не уходит (issue/11348, issue/11350).
  const recordType = useMemo(() => {
    const type = record?.RecordType;

    if (mode !== 'edit' || !type || type === RecordTypeEnum.NONE) return null;

    return type;
  }, [mode, record?.RecordType]);

  const employeeUserId = useMemo(
    () => employee?.UserId ?? null,
    [employee?.UserId]
  );

  const recordRange = useMemo(() => {
    if (!formValues?.Date) return { timeS: '', timeE: '' };

    return {
      timeS: `${formValues.Date.format('YYYY-MM-DD')}T${
        formValues.TimeStart
      }:00`,
      timeE: `${formValues.Date.format('YYYY-MM-DD')}T${formValues.TimeEnd}:00`,
    };
  }, [formValues?.Date, formValues?.TimeStart, formValues?.TimeEnd]);

  const selectedServicesIds = useMemo(() => {
    const selectedServices = formValues?.services || [];
    const filteredSelectedServicesIds = selectedServices
      .filter((item) => item !== null && item !== undefined)
      ?.map((item) => item?.service)
      ?.filter((item) => item !== null && item !== undefined)
      ?.map((item) => +item);

    return filteredSelectedServicesIds;
  }, [formValues?.services]);

  const onChangeCurrentClient = useCallback(
    (id: number | null, name?: string | null) => {
      dispatch(setClientId(id));
      dispatch(setClientName(name || null));
    },
    [dispatch]
  );

  const prepareRecordCreationHandler = useCallback(
    async (args: NServices.PrepareRecordCreationHandlerProps) => {
      try {
        const response = await prepareRecordCreation({
          ...args,
          ...recordParams,
        }).unwrap();

        dispatch(setPrepareRecordCreationResponse(response));
        return response;
      } catch (error) {
        showNotification(error, WARNING);
      }
    },
    [recordParams, prepareRecordCreation, showNotification, dispatch]
  );

  const checkRecordTimesHandler = useCallback(async () => {
    if (
      isLoading ||
      !isOpen ||
      !isInitialized ||
      !recordRange.timeS ||
      !recordRange.timeE
    ) {
      return;
    }

    const recordId = record?.Id || null;
    const branchId = record?.BranchId || recordParams.ActiveBranchId;

    try {
      const response = await checkRecordTimes({
        ...recordParams,
        ...recordRange,
        recordId,
        employeeId,
        branchId,
      }).unwrap();

      dispatch(setWarnings(response.Warnings));

      return response.Warnings;
    } catch (error) {
      showNotification(error, WARNING);
    }
  }, [
    record,
    recordParams,
    recordRange,
    showNotification,
    checkRecordTimes,
    isLoading,
    isOpen,
    isInitialized,
    employeeId,
    dispatch,
  ]);

  const getSelectedServiceEmployeePayoff = useCallback(
    (
      services: Service[],
      allEmployeeServices: NEmployees.ModelClientService[]
    ) => {
      const fullEmployeePayoff = services.reduce((acc, service) => {
        const _service = allEmployeeServices?.find(
          ({ Id }) => Id === Number(service?.service)
        );

        if (!service) return acc;

        if (_service?.SalaryFormat === 'FixedRate') {
          return acc + _service?.SalaryAmount;
        } else {
          const quantity = service?.quantity >= 0 ? service?.quantity : 0;
          const price = service?.price >= 0 ? service?.price : 0;
          const discount =
            service?.discountCurrency >= 0 ? service?.discountCurrency : 0;
          const total = +(quantity * price - discount)?.toFixed(2);

          const percent = _service?.SalaryAmount;

          return acc + ((percent || 0) * total) / 100;
        }
      }, 0);

      return fullEmployeePayoff;
    },
    []
  );

  const getEmployeesByServicesHandler = useCallback(async () => {
    const date = moment(record?.TimeS || employee?.Date);

    if (!date.isValid()) {
      showNotification(
        'Ошибка с выбранной датой. Обратитесь в техподдержку',
        FRONT_WARNING
      );
      return;
    }

    if (!selectedServicesIds.length) {
      setServiceEmployees([]);
      return;
    }

    if (!recordActiveBranchId) {
      showNotification(
        'Произошла ошибка при определении идентификатора филиала',
        FRONT_WARNING
      );
      return;
    }

    try {
      const response = await getEmployeesByServices({
        branchId: recordActiveBranchId,
        ActiveBranchId: recordActiveBranchId,
        date: date.format('YYYY-MM-DD'),
        data: selectedServicesIds,
        recordId: record?.Id ?? 0,
        logs: { isLogged: true, userId, database },
      }).unwrap();
      setServiceEmployees(response);
    } catch (error) {
      showNotification(error, FRONT_WARNING);
    }
  }, [
    selectedServicesIds,
    record?.Id,
    userId,
    database,
    recordActiveBranchId,
    showNotification,
    record?.TimeS,
    employee?.Date,
    getEmployeesByServices,
  ]);

  const closeRecordDetails = useCallback(() => {
    resetClientSearch();
    resetPhoneClientSearch();
    phoneClientRef.current = null;
    resetFields();
    setBranchUsers([]);
    setWarehouses([]);
    setTags([]);
    setServiceOptions([]);
    setSavedData(null);
    dispatch(setHasChanges(false));
    dispatch(setIsInitialized(false));
  }, [resetClientSearch, resetPhoneClientSearch, resetFields, dispatch]);

  const resetForm = useCallback(() => {
    closeRecordDetails();
    onChangeCurrentClient(null, null);
    if (clientIdQueryParam) {
      const params = new URLSearchParams(searchParams);
      params.delete('clientId');
      setSearchParams(params, { replace: true });
    }
  }, [
    closeRecordDetails,
    clientIdQueryParam,
    searchParams,
    setSearchParams,
    onChangeCurrentClient,
  ]);

  const initUnsavedData = useCallback(
    (data: RecordDetailsForm) => {
      return new Promise<void>((resolve) => {
        setTimeout(() => {
          setSavedData(data);
          dispatch(setIsInitialized(true));

          resolve();
        }, 100);
      });
    },
    [dispatch]
  );

  const [
    getClientServicesByEmployeeId,
    {
      isFetching: isFetchingGetClientServicesByEmployeeId,
      isLoading: isLoadingGetClientServicesByEmployeeId,
    },
  ] = useLazyGetClientServicesByEmployeeIdQuery();

  const onChangeServiceOptions = useCallback(
    (services: ClientServiceInterface[]) => {
      const serviceOptions = getServicesOptions(services);

      setServiceOptions(serviceOptions);
      setClientServices(services);
    },
    [setServiceOptions]
  );

  const getRecordByIdHandler = useCallback(
    async (recordId: number) => {
      try {
        const recordResponse = await getRecordById({
          ActiveBranchId: recordActiveBranchId,
          recordId,
        }).unwrap();

        return recordResponse as RecordInterface;
      } catch (error) {
        showNotification(error, WARNING);

        return null;
      }
    },
    [showNotification, recordActiveBranchId, getRecordById]
  );

  const getClientServicesByEmployeeIdHandler = useCallback(
    async (employeeId: number) => {
      try {
        const response = await getClientServicesByEmployeeId({
          ActiveBranchId: recordParams.ActiveBranchId,
          employeeId,
        }).unwrap();

        return response;
      } catch (error) {
        showNotification(error, WARNING);
      }
    },
    [recordParams, getClientServicesByEmployeeId, showNotification]
  );

  const onSelectEmployeeForService = useCallback(
    async (employeeId: number) => {
      const response = await getClientServicesByEmployeeIdHandler(employeeId);

      if (!response) return;

      onChangeServiceOptions(
        response as Array<unknown> as Array<ClientServiceInterface>
      );
    },
    [getClientServicesByEmployeeIdHandler, onChangeServiceOptions]
  );

  const allEmployeeServicesHandler = useCallback(
    async (employeeId: number) => {
      const response = await getClientServicesByEmployeeIdHandler(employeeId);

      return response ?? [];
    },
    [getClientServicesByEmployeeIdHandler]
  );

  const openRecordById = useCallback(
    async (recordId: number) => {
      dispatch(setIsLoading(true));

      try {
        const recordResponse = await getRecordByIdHandler(recordId);

        if (!recordResponse) {
          showNotification(
            'Непредвиденная ошибка при открытии записи: не найдено',
            FRONT_WARNING
          );
          return;
        }

        const {
          NameFirst,
          NameLast,
          PhoneCountryId,
          Phone,
          IsPhoneVerified,
          ClientId,
          TimeS,
          TimeE,
          RecordColor,
          Tags: RecordTags,
          Comment,
          Status,
          ClientServices: RecordServices,
          IsEmployeeLocked,
          EmployeeId,
          EmployeePayoff,
          IsFullyPaid,
        } = recordResponse;

        dispatch(setIsFullyPaid(IsFullyPaid));

        if (!EmployeeId) {
          showNotification(
            'Непредвиденная ошибка при открытии записи: не найден исполнитель',
            FRONT_WARNING
          );
          return;
        }

        const allEmployeeServices = await allEmployeeServicesHandler(
          EmployeeId
        );

        setAllEmployeeServices(allEmployeeServices);

        const prepareRecordCreationResponse =
          await prepareRecordCreationHandler({
            employeeId: EmployeeId,
            dateTime: moment(TimeS).format('HH:mm'),
            recordId,
          });

        if (!prepareRecordCreationResponse) return;

        const {
          Tags,
          ClientServices,
          BranchUsers,
          BranchEmployees,
          Warehouses,
        } = prepareRecordCreationResponse;

        setBranchUsers(BranchUsers);
        setBranchEmployees(BranchEmployees);
        setWarehouses(Warehouses ?? []);
        onChangeServiceOptions(ClientServices as Array<ClientServiceInterface>);
        setTags(Tags);
        onChangeCurrentClient(
          ClientId,
          `${NameLast || ''} ${NameFirst}`.trim()
        );
        setSelectedEmployee({
          RecordId: recordResponse.Id,
          EmployeeId: EmployeeId,
        });
        const DurationHour = moment(TimeE).diff(moment(TimeS), 'hours');
        const DurationMinute =
          moment(TimeE).diff(moment(TimeS), 'minutes') % 60;
        const services: Array<Service> =
          mapRecordServicesToFormServices(RecordServices);
        const emptyEmployeePayoff =
          EmployeePayoff === null || EmployeePayoff === undefined;
        const serviceEmployeePayoff = getSelectedServiceEmployeePayoff(
          services,
          allEmployeeServices
        );

        const data: RecordDetailsForm = {
          Phone: {
            CountryId: PhoneCountryId || 1,
            Phone: Phone || '',
          },
          IsPhoneVerified: IsPhoneVerified,
          NameFirst: NameFirst || '',
          NameLast: NameLast || '',
          Employee: EmployeeId,
          IsLocked: IsEmployeeLocked,
          Color: RecordColor ?? null,
          Tags: (RecordTags || [])?.map(({ Id }) => Id),
          Comment: Comment || '',
          DurationHour,
          DurationMinute,
          Date: dayjs(TimeS),
          TimeStart: moment(TimeS).format('HH:mm'),
          TimeEnd: moment(TimeE).format('HH:mm'),
          Status: Status as ClientStatusEnum,
          services,
          products: mapRecordProductsToDraftItems(recordResponse.Products),
          subscriptions: mapRecordSubscriptionsToDraftItems(
            recordResponse.Subscriptions
          ),
          // Сертификаты записи бэкенд пока не отдаёт: блок открывается пустым.
          certificates: [],
          EmployeePayoff:
            emptyEmployeePayoff && serviceEmployeePayoff > 0
              ? serviceEmployeePayoff
              : EmployeePayoff,
        };
        const date =
          !!searchParams.get('date') &&
          moment(searchParams.get('date')).isValid()
            ? (searchParams.get('date') as string)
            : moment().format('YYYY-MM-DD');
        const recordClientName =
          `${recordResponse?.NameLast || ''} ${
            recordResponse?.NameFirst
          }`.trim() || null;

        dispatch(setClientId(ClientId));
        dispatch(setClientName(recordClientName));
        dispatch(
          setSavedClient({ clientId: ClientId, clientName: recordClientName })
        );

        setEqualEmployeePayoff(
          emptyEmployeePayoff || EmployeePayoff === serviceEmployeePayoff
        );
        dispatch(setDate(date));
        dispatch(setRecord(recordResponse as RecordInterface));
        savedSubscriptionsRef.current = null;
        dispatch(setRecordTab(MenuTabEnum.RecordDetails));
        dispatch(setHasChanges(false));
        // Номер записи — Phone1 её клиента (beauty-crm LogicRecords.cs:2523):
        // открытие записи поиск по номеру не запускает.
        resetPhoneClientSearch();
        phoneClientRef.current = ClientId
          ? createPhoneClient(ClientId, Phone, NameFirst, NameLast)
          : null;
        setFieldsValue(data);
        await initUnsavedData(data);

        if (searchParams.get('recordId')) {
          const newParams = new URLSearchParams(searchParams);
          newParams.delete('recordId');
          setSearchParams(newParams, { replace: true });
        }
        dispatch(setRecordIdForOpen(null));
      } catch (error) {
        showNotification(error, 'warning');
      } finally {
        dispatch(setIsLoading(false));
      }
    },
    [
      dispatch,
      initUnsavedData,
      onChangeCurrentClient,
      onChangeServiceOptions,
      prepareRecordCreationHandler,
      setFieldsValue,
      showNotification,
      searchParams,
      setSearchParams,
      getRecordByIdHandler,
      getSelectedServiceEmployeePayoff,
      allEmployeeServicesHandler,
      resetPhoneClientSearch,
    ]
  );

  // Услуги с абонемента принадлежат клиенту, у которого взят абонемент: при
  // смене клиента их убираем, обычные услуги остаются.
  const removeClientSubscriptionServicesHandler = useCallback(() => {
    const currentServices: Service[] = getFieldValue('services') || [];
    const nextServices = removeClientSubscriptionServices(currentServices);

    if (!nextServices) return;

    setFieldsValue({
      services: nextServices,
      ...getServicesDurationFields({
        nextServices,
        previousServices: currentServices,
        durationHour: getFieldValue('DurationHour') || 0,
        durationMinute: getFieldValue('DurationMinute') || 0,
        timeStart: getFieldValue('TimeStart'),
      }),
    });
    showNotification(
      'Услуги с абонемента прежнего клиента убраны из записи',
      FRONT_WARNING
    );
  }, [getFieldValue, setFieldsValue, showNotification]);

  const onSelectClient = useCallback(
    (value: number) => {
      const selectedClient = clientSearchResults.find(({ Id }) => Id === value);

      if ((selectedClient?.Id ?? 0) !== currentClientId) {
        removeClientSubscriptionServicesHandler();
      }

      // Клиент из «Поиск» снимает поиск по номеру: его номер встаёт в поле
      // телефона, поздний ответ на прежний номер клиента не снимет.
      resetPhoneClientSearch();

      if (!selectedClient) {
        phoneClientRef.current = null;
        onChangeCurrentClient(0, null);
        resetFields(['NameFirst', 'NameLast', 'IsPhoneVerified']);

        return;
      }

      const { Id, NameFirst, NameLast, IsPhoneVerified, Phone1, Phone2 } =
        selectedClient;
      const selectedPhone = Phone1 ?? Phone2 ?? formValues?.Phone?.Phone;

      phoneClientRef.current = createPhoneClient(
        Id,
        selectedPhone,
        NameFirst,
        NameLast,
        Phone1
      );
      onChangeCurrentClient(Id, `${NameLast || ''} ${NameFirst}`.trim());
      setClientSearchTerm('');
      setFieldsValue({
        NameFirst: NameFirst ?? '',
        NameLast: NameLast ?? '',
        IsPhoneVerified: IsPhoneVerified ?? false,
        Phone: {
          ...(formValues?.Phone ?? {}),
          Phone: selectedPhone ?? formValues?.Phone?.Phone,
        },
      });
      clearPhoneClientError();
    },
    [
      resetFields,
      clientSearchResults,
      setClientSearchTerm,
      setFieldsValue,
      formValues,
      onChangeCurrentClient,
      currentClientId,
      removeClientSubscriptionServicesHandler,
      resetPhoneClientSearch,
      clearPhoneClientError,
    ]
  );

  // Клиент из меню поля «Мобильный телефон» (task/11629). В поле встаёт
  // Phone1 клиента, даже если клиент найден по Phone2: UpdateRecord пишет
  // номер записи в Phone1 клиента.
  const onSelectPhoneClient = useCallback(
    (clientId: number) => {
      const selectedClient = phoneClients.find(({ Id }) => Id === clientId);

      if (!selectedClient) return;

      const {
        Id,
        NameFirst,
        NameLast,
        IsPhoneVerified,
        Phone1,
        Phone1CountryId,
      } = selectedClient;
      const currentPhone: RecordDetailsForm['Phone'] | undefined =
        getFieldValue('Phone');
      // Клиент без Phone1 найден по Phone2: номер в поле остаётся. И1 здесь
      // не действует (решение техлида, раунд 3): Phone1 у клиента пуст,
      // UpdateRecord запишет в него этот номер, номер не теряется.
      const selectedPhone = Phone1 || currentPhone?.Phone || '';
      const selectedCountryId = getRecordPhoneCountryId(
        Phone1 ? Phone1CountryId : null,
        getRecordPhoneCountryId(currentPhone?.CountryId)
      );

      if (Id !== currentClientId) {
        removeClientSubscriptionServicesHandler();
      }

      resetPhoneClientSearch();
      phoneClientRef.current = createPhoneClient(
        Id,
        selectedPhone,
        NameFirst,
        NameLast,
        Phone1
      );
      onChangeCurrentClient(Id, `${NameLast || ''} ${NameFirst || ''}`.trim());
      setFieldsValue({
        NameFirst: NameFirst ?? '',
        NameLast: NameLast ?? '',
        IsPhoneVerified: IsPhoneVerified ?? false,
        Phone: { CountryId: selectedCountryId, Phone: selectedPhone },
      });
      clearPhoneClientError();
    },
    [
      clearPhoneClientError,
      phoneClients,
      getFieldValue,
      currentClientId,
      removeClientSubscriptionServicesHandler,
      resetPhoneClientSearch,
      onChangeCurrentClient,
      setFieldsValue,
    ]
  );

  // Номер изменён, клиента с ним нет или он не выбран: клиент снимается.
  // Имя и фамилия прежнего клиента очищаются, поле с правкой пользователя
  // остаётся. «Номер подтверждён» относится к клиенту и сбрасывается.
  const detachPhoneClient = useCallback(() => {
    const previousClient = phoneClientRef.current;

    phoneClientRef.current = null;

    if (!currentClientId && !previousClient) return;

    const nameFirst: string = getFieldValue('NameFirst') ?? '';
    const nameLast: string = getFieldValue('NameLast') ?? '';

    removeClientSubscriptionServicesHandler();
    onChangeCurrentClient(0, null);
    setFieldsValue({
      IsPhoneVerified: false,
      ...(previousClient &&
        nameFirst === previousClient.nameFirst && { NameFirst: '' }),
      ...(previousClient &&
        nameLast === previousClient.nameLast && { NameLast: '' }),
    });
  }, [
    currentClientId,
    getFieldValue,
    removeClientSubscriptionServicesHandler,
    onChangeCurrentClient,
    setFieldsValue,
  ]);

  // Ошибка поля телефона перед сохранением: номер изменён, а клиент для
  // него не выбран, или номер выбранного клиента изменён в обход поиска.
  // Без права «Показывать контакты» бэкенд клиента не меняет
  // (LogicRecords.cs:1104-1106), поиска нет, ошибки нет.
  // Последний барьер И1 для любых путей: ClientId уходит в UpdateRecord,
  // только если номер в поле — номер этого клиента. Клиент записи сменился
  // в обход поля телефона («Уйти без изменений» вернул клиента записи) —
  // ошибка. Клиенту с пустым Phone1 номер записи не сверяется.
  const getPhoneClientError = useCallback(
    (phone: string | null | undefined) => {
      if (!rightToViewContacts) return null;

      const phoneDigits = getComparablePhone(phone);

      // Ошибка поиска относится к номеру поиска. Номер стал другим без
      // нового поиска (стёрли цифру, сменили страну) — он неполный, его
      // ошибку покажет само поле телефона.
      if (phoneClientSearchError && phoneDigits === searchedPhone) {
        return phoneClientSearchError;
      }

      if (!currentClientId) return null;

      const phoneClient = phoneClientRef.current;

      if (!phoneClient) {
        return phoneDigits ? PHONE_CLIENT_FAILED_ERROR : null;
      }

      if (
        phoneClient.clientId !== currentClientId ||
        (!phoneClient.isPhoneEmpty && phoneClient.phone !== phoneDigits)
      ) {
        return PHONE_CLIENT_FAILED_ERROR;
      }

      return null;
    },
    [
      rightToViewContacts,
      phoneClientSearchError,
      searchedPhone,
      currentClientId,
    ]
  );

  // Правило поля «Мобильный телефон»: «Сохранить» показывает ошибку у поля
  // и в сводке «Запись не сохранена — проверьте поля».
  const phoneClientRules = useMemo<Rule[]>(
    () => [
      {
        validator: (_: RuleObject, value?: RecordDetailsForm['Phone']) => {
          const error = getPhoneClientError(value?.Phone);

          return error ? Promise.reject(error) : Promise.resolve();
        },
      },
    ],
    [getPhoneClientError]
  );

  const onSelectStatus = useCallback(
    (status: ClientStatusEnum) => {
      setFieldValue('Status', status);
    },
    [setFieldValue]
  );

  const importantEmployeeHandler = useCallback(() => {
    setFieldValue('IsLocked', !!selectedImportantEmployeeTag);
  }, [selectedImportantEmployeeTag, setFieldValue]);

  const checkDisabledStatus = useCallback(() => {
    return false;
  }, []);

  // Сохранить изменённые значения важных доп. полей клиента вместе с
  // записью. Ошибка не должна ронять сохранение самой записи — только
  // нотификация; введённые значения остаются в форме.
  const saveClientImportantFieldValues = useCallback(
    async (
      clientId: number | null | undefined,
      fieldDrafts: Record<string, string> | undefined
    ) => {
      const branchId = recordActiveBranchId ?? params.ActiveBranchId;
      if (!clientId || !branchId || !fieldDrafts) return;

      try {
        const currentValues = await getClientFieldValues({
          ActiveBranchId: branchId,
          clientId,
        }).unwrap();

        const hasChanges = currentValues.some((field) => {
          if (!field.IsImportant) return false;
          const draft = fieldDrafts[String(field.FieldId)];
          return draft !== undefined && draft !== (field.Value ?? '');
        });

        if (!hasChanges) return;

        // SetClientFieldValues ждёт ПОЛНЫЙ список значений клиента:
        // непереданные поля бэк стирает, поэтому отправляем все поля,
        // подставляя черновики только в изменённые важные.
        const FieldValues: ModelClientFieldValue[] = currentValues.map(
          (field) => {
            const draft = field.IsImportant
              ? fieldDrafts[String(field.FieldId)]
              : undefined;

            return {
              ...field,
              ClientId: clientId,
              Value: draft !== undefined ? draft : field.Value,
            };
          }
        );

        await setClientFieldValues({
          ActiveBranchId: branchId,
          data: { ClientId: clientId, FieldValues },
        }).unwrap();
      } catch (error) {
        showNotification(error, WARNING);
      }
    },
    [
      recordActiveBranchId,
      params,
      getClientFieldValues,
      setClientFieldValues,
      showNotification,
    ]
  );

  // Абонементы сохраняются отдельным методом после сохранения записи (ТЗ
  // us/11427), только если в них что-то изменилось: каждый вызов со строкой
  // Id = 0 — новая продажа. Ошибка не отменяет уже сохранённую запись:
  // возвращаем false, правки остаются в форме для повторного сохранения.
  const saveRecordSubscriptions = useCallback(
    async (
      recordId: number,
      items: SubscriptionSaleDraftItem[] | undefined,
      savedItems: SubscriptionSaleDraftItem[]
    ): Promise<boolean> => {
      if (!hasRecordSubscriptionsChanges(items, savedItems)) return true;

      if (!recordId) {
        showNotification(RECORD_SUBSCRIPTIONS_NO_RECORD_ERROR, FRONT_WARNING);
        return false;
      }

      try {
        const response = await setRecordSubscriptions({
          ActiveBranchId: recordActiveBranchId,
          data: {
            RecordId: recordId,
            Subscriptions: buildRecordSubscriptionsPayload(items),
          },
        }).unwrap();

        const responseItems = mapRecordSubscriptionsToDraftItems(response);

        savedSubscriptionsRef.current = { recordId, items: responseItems };
        setFieldValue('subscriptions', responseItems);

        return true;
      } catch (error) {
        showNotification(
          getRecordSubscriptionsSaveErrorText(handleCatchError(error)),
          FRONT_WARNING
        );

        return false;
      }
    },
    [
      recordActiveBranchId,
      setRecordSubscriptions,
      setFieldValue,
      showNotification,
    ]
  );

  const onFinish = useCallback(
    async (values: RecordDetailsForm, nextRecordId?: number) => {
      dispatch(setIsLoading(true));
      const {
        Date,
        TimeStart,
        TimeEnd,
        NameLast,
        NameFirst,
        Status,
        IsPhoneVerified,
        DurationMinute,
        DurationHour,
        Comment,
        Employee,
        IsLocked,
        Tags,
        Color,
        Phone: { Phone, CountryId },
        services,
        products,
        subscriptions,
        EmployeePayoff,
      } = values;

      const productsValidationError = validateRecordProducts(products);

      if (productsValidationError) {
        showNotification(productsValidationError, FRONT_WARNING);
        dispatch(setIsLoading(false));
        return;
      }

      const subscriptionsValidationError =
        validateRecordSubscriptions(subscriptions);

      if (subscriptionsValidationError) {
        showNotification(subscriptionsValidationError, FRONT_WARNING);
        dispatch(setIsLoading(false));
        return;
      }

      // Услуга с абонемента без пары у исполнителя молча выпала бы из
      // запроса: запись не сохраняем, пока её не уберут или не сменят
      // исполнителя.
      const notProvidedSubscriptionServices =
        getNotProvidedSubscriptionServices(services, clientServices);

      if (notProvidedSubscriptionServices.length) {
        showNotification(
          `Исполнитель не оказывает услуги с абонемента: ${getServiceNames(
            notProvidedSubscriptionServices
          )}. Уберите их из записи или выберите другого исполнителя`,
          FRONT_WARNING
        );
        dispatch(setIsLoading(false));
        return;
      }

      // Сохранённую услугу нельзя перевести между «платной» и «с абонемента»:
      // UpdateRecord не переносит эту смену на единицы абонемента и цену.
      const changedSavedServices = getChangedSavedServices(
        services,
        savedData?.services ?? []
      );

      if (changedSavedServices.length) {
        showNotification(
          getChangedSavedServicesMessage(changedSavedServices),
          FRONT_WARNING
        );
        dispatch(setIsLoading(false));
        return;
      }
      const { errorMessage, isValid } = phoneValidation;
      const phoneErrorMessage =
        errorMessage || (!isValid ? 'Обязательное поле' : '');

      if (phoneErrorMessage) {
        setFields([{ name: 'Phone', errors: [phoneErrorMessage] }]);
        dispatch(setIsLoading(false));
        return;
      }

      const phoneClientError = getPhoneClientError(Phone);

      if (phoneClientError) {
        setFields([{ name: 'Phone', errors: [phoneClientError] }]);
        scrollToField(['Phone'], { behavior: 'smooth', block: 'center' });
        dispatch(setIsLoading(false));
        return;
      }

      const warnings = await checkRecordTimesHandler();

      if (warnings && warnings?.length > 0) {
        const actionWarnings = await confirmWarnings(warnings);

        if (actionWarnings === 'cancel') {
          dispatch(setIsLoading(false));
          return;
        }
      }

      const DateFormatted = Date?.format('YYYY-MM-DD');
      const recordTimeS = moment(
        `${DateFormatted} ${TimeStart}`,
        'YYYY-MM-DD HH:mm'
      );

      const recordTimeE = moment(
        `${DateFormatted} ${TimeEnd}`,
        'YYYY-MM-DD HH:mm'
      );

      // Без даты или времени начала moment невалиден, а format() отдаёт строку
      // 'Invalid date' — UpdateRecord падает с 400 по $.TimeS (issue/10712).
      if (!recordTimeS.isValid() || !recordTimeE.isValid()) {
        dispatch(setIsLoading(false));
        return showNotification('Выберите дату и время записи', FRONT_WARNING);
      }

      const TimeS = recordTimeS.format('YYYY-MM-DDTHH:mm:ss');
      const TimeE = recordTimeE.format('YYYY-MM-DDTHH:mm:ss');

      const Duration = DurationHour * 60 + DurationMinute;

      const ClientServices = buildRecordClientServices(
        services,
        clientServices
      );

      const Products = buildRecordProductsPayload(products);

      const initialData: UpdateRecordDataInterface = {
        Id: mode === 'create' ? 0 : record?.Id || 0,
        ClientId: currentClientId ?? 0,
        PhoneCountryId: CountryId,
        BranchId:
          mode === 'create' ? recordActiveBranchId : record?.BranchId || null,
        EmployeeId: Employee,
        IsEmployeeLocked: Boolean(IsLocked),
        IsDeleted: false,
        Tags: Tags.map((id) => ({ id })),
        RecordColor: rgbToHex(Color),
        TimeS,
        TimeE,
        NameLast,
        NameFirst,
        Phone,
        IsPhoneVerified,
        Status,
        Duration,
        Comment,
        Color,
        ClientServices,
        Products,
        EmployeePayoff:
          (EditEmployeePayoff
            ? Number(EmployeePayoff)
            : Number(record?.EmployeePayoff)) || 0,
      };

      if (!Employee) {
        dispatch(setIsLoading(false));
        return showNotification(
          'Выберите, пожалуйста, сотрудника',
          'frontWarning'
        );
      }

      const otherEmployees = additionalEmployees.filter(
        (id) => id !== null && id !== undefined
      );

      try {
        // Бэк конвертирует TimeS/TimeE записи из UTC и обратно по таймзоне
        // филиала из ActiveBranchId запроса, поэтому сохранять надо тем же
        // филиальным контекстом, каким запись читали (getRecordByIdHandler):
        // иначе показанное в форме время истолкуется как время другой
        // таймзоны и запись уедет на разницу между ними. Филиал самой записи
        // бэк при редактировании не переписывает.
        // После успешного UpdateRecord предел количества услуг с абонемента
        // считается от сохранённых услуг, даже если следующий шаг сохранения
        // упадёт (task/11536).
        const firstRecordResponse = await updateRecordWithSavedServices({
          updateRecord: () =>
            updateRecord({
              ActiveBranchId: recordActiveBranchId,
              data: {
                ...initialData,
                EmployeeId: Employee,
                Id: mode === 'edit' ? record?.Id || 0 : 0,
                ClientServices: initialData.ClientServices,
                // UpdateRecord перезаписывает запись целиком: без RecordType
                // тип существующей записи сбрасывался в «Отсутствует»
                // (issue/11358). Новой записи тип не передаём — его считает
                // бэк (issue/11348).
                ...(mode === 'edit' && {
                  RecordType: record?.RecordType ?? null,
                }),
              },
            }).unwrap(),
          services,
          onServicesSaved: (nextSavedServices) =>
            setSavedData((current) =>
              replaceSavedServices(current, nextSavedServices)
            ),
        });

        // Абонементы — только на первой записи: записи доп. исполнителей
        // ниже создаются без услуг и товаров.
        const savedSubscriptions = getSavedRecordSubscriptions({
          isEditMode: mode === 'edit',
          recordId: record?.Id,
          recordSubscriptions: record?.Subscriptions,
          lastSaved: savedSubscriptionsRef.current,
        });
        const isSubscriptionsSaved = await saveRecordSubscriptions(
          firstRecordResponse.Id,
          subscriptions,
          savedSubscriptions
        );

        // UpdateRecord занимает и освобождает услуги абонементов клиента:
        // остаток в «Абонементах клиента» устарел. Сброс — после
        // SetRecordSubscriptions, чтобы последний запрос остатка ушёл после
        // обоих методов.
        dispatch(invalidateClientSubscriptions());

        const { ClientId, NameLast, NameFirst } = firstRecordResponse;
        const nextDay = moment(TimeS).format('YYYY-MM-DD');

        // Окно тегов показывается только при создании записи и только если
        // организация его включила (ТЗ us/11144, п. 4 и 6). Запись к этому
        // моменту уже создана: окно её не отменяет, а только дописывает теги.
        // День записи нужен окну, чтобы после сохранения тегов обновить журнал.
        if (
          mode !== 'edit' &&
          prepareRecordCreationResponse?.IsRecordTagsWindow &&
          firstRecordResponse.Id
        ) {
          dispatch(
            openRecordTagsWindow({
              recordId: firstRecordResponse.Id,
              date: nextDay,
            })
          );
        }

        await saveClientImportantFieldValues(
          ClientId ?? currentClientId,
          values.ClientFieldValues
        );

        const otherEmployeesPromises = otherEmployees.map((id) =>
          updateRecord({
            ActiveBranchId: recordActiveBranchId,
            data: {
              ...initialData,
              ClientId: ClientId || 0,
              EmployeeId: id,
              Id: 0,
              ClientServices: [],
              Products: [],
            },
          }).unwrap()
        );

        await Promise.all(otherEmployeesPromises).catch((error) => {
          throw new Error(error?.data);
        });

        if (!isSubscriptionsSaved) {
          // Запись сохранена, абонементы — нет: окно остаётся открытым и
          // переходит в редактирование, чтобы повторное «Сохранить» не
          // создало запись заново.
          dispatch(setNextTab(null));
        } else if (nextTab) {
          dispatch(setRecordTab(nextTab));
          dispatch(setNextTab(null));
          dispatch(setIsInitialized(false));
          dispatch(setHasChanges(false));
        } else if (nextRecordId) {
          openRecordById(nextRecordId);
          dispatch(setIsInitialized(false));
          dispatch(setHasChanges(false));
        } else {
          dispatch(closeRecordModal());
        }

        const savedClientName = `${NameLast || ''} ${NameFirst}`.trim();

        // Номер записи после сохранения — Phone1 её клиента.
        phoneClientRef.current = ClientId
          ? createPhoneClient(
              ClientId,
              firstRecordResponse.Phone ?? Phone,
              NameFirst,
              NameLast
            )
          : null;
        dispatch(setRecord(firstRecordResponse || null));
        dispatch(setMode('edit'));
        onChangeCurrentClient(ClientId, savedClientName);
        dispatch(
          setSavedClient({
            clientId: ClientId,
            clientName: savedClientName || null,
          })
        );

        const moreOneEmployee = otherEmployeesPromises.length > 0;

        const action = mode === 'edit' ? 'обновлен' : 'создан';

        const title = `Запис${moreOneEmployee ? 'и' : 'ь'} успешно ${action}${
          moreOneEmployee ? 'ы' : 'а'
        }`;

        const formValues = getFieldsValue();

        // Несохранённые правки абонементов остаются несохранёнными данными:
        // закрытие окна спросит, сохранить ли их.
        await initUnsavedData(
          isSubscriptionsSaved
            ? formValues
            : { ...formValues, subscriptions: savedSubscriptions }
        );

        showNotification(title, SUCCESS);

        const today = moment().format('YYYY-MM-DD');

        if (pathname === CALENDAR_PATH) {
          if (today !== nextDay) {
            setSearchParams(
              { ...searchParams, date: nextDay },
              { replace: true }
            );
          } else {
            const newParams = new URLSearchParams(searchParams);
            newParams.delete('date');
            setSearchParams(newParams, { replace: true });
          }
        }

        afterSave(nextDay);
      } catch (error) {
        showNotification(error, WARNING);
      } finally {
        dispatch(setIsLoading(false));
      }
    },
    [
      afterSave,
      additionalEmployees,
      currentClientId,
      dispatch,
      getFieldsValue,
      mode,
      openRecordById,
      prepareRecordCreationResponse?.IsRecordTagsWindow,
      record,
      showNotification,
      clientServices,
      initUnsavedData,
      nextTab,
      setSearchParams,
      updateRecord,
      onChangeCurrentClient,
      recordActiveBranchId,
      phoneValidation,
      checkRecordTimesHandler,
      setFields,
      confirmWarnings,
      saveClientImportantFieldValues,
      saveRecordSubscriptions,
      pathname,
      searchParams,
      EditEmployeePayoff,
      savedData,
      getPhoneClientError,
      scrollToField,
    ]
  );

  const getClientByIdHandler = useCallback(
    async (clientId?: number) => {
      if (!recordActiveBranchId) return;
      if (!clientId) return;

      try {
        const response = await getClientById({
          clientId,
          ActiveBranchId: recordActiveBranchId,
        }).unwrap();

        return response;
      } catch (error) {
        showNotification(error, WARNING);
        return null;
      }
    },
    [getClientById, showNotification, recordActiveBranchId]
  );

  const initCreateRecordForm = useCallback(
    async (employeeUserId: number) => {
      dispatch(setIsLoading(true));
      const selectedTime =
        !!time && moment(time, 'HH:mm').isValid() ? time : '10:00';

      const currentDay = moment(`${date} ${selectedTime}`, 'YYYY-MM-DD HH:mm');

      const TimeStart = currentDay.format('HH:mm');
      // Журнал передаёт конец предложенной записи: он равен длительности
      // секции из настроек журнала (issue/11152) и обрезан по занятому
      // времени, если шаг сетки занят записью частично (issue/11151).
      // Остальные точки входа (карточка лида, окно исполнителей) конца не
      // передают — им остаётся прежний час.
      const suggestedEndMoment =
        !!endTime && moment(endTime, 'HH:mm').isValid()
          ? moment(`${date} ${endTime}`, 'YYYY-MM-DD HH:mm')
          : null;
      const endMoment =
        suggestedEndMoment && suggestedEndMoment.isAfter(currentDay)
          ? suggestedEndMoment
          : currentDay.clone().add(1, 'hour');
      const TimeEnd = endMoment.format('HH:mm');
      const durationMinutes = Math.max(
        endMoment.diff(currentDay, 'minutes'),
        0
      );

      const Date = dayjs(date);

      if (date === null) {
        showNotification(
          'Ошибка с выбранной датой. Обратитесь в техподдержку',
          FRONT_WARNING
        );
        dispatch(setIsLoading(false));
        return;
      }

      const prepareRecordCreationResponse = await prepareRecordCreationHandler({
        employeeId: employeeUserId,
        dateTime: date,
      });

      const allEmployeeServices =
        employeeUserId > 0
          ? await allEmployeeServicesHandler(employeeUserId)
          : [];

      setAllEmployeeServices(allEmployeeServices);

      if (!prepareRecordCreationResponse) {
        dispatch(setIsLoading(false));
        return;
      }

      const initialClientId = currentClientId || clientIdQueryParam;
      const clientResponse = await getClientByIdHandler(initialClientId);
      dispatch(setClientId(initialClientId || null));

      const { BranchEmployees, Tags, ClientServices, BranchUsers, Warehouses } =
        prepareRecordCreationResponse;

      // Prefer the active branch country; fall back to the default (+7) when
      // it is not yet resolved.
      const CountryId = getRecordPhoneCountryId(activeBranchCountry);

      const emptyData: RecordDetailsForm = {
        Status: ClientStatusEnum.WAITING,
        Phone: { CountryId, Phone: '' },
        IsPhoneVerified: false,
        NameFirst: '',
        NameLast: '',
        Date,
        TimeStart,
        TimeEnd,
        DurationHour: Math.floor(durationMinutes / 60),
        DurationMinute: durationMinutes % 60,
        Comment: '',
        Tags: [],
        Color: null,
        IsLocked: false,
        Employee: employeeUserId > 0 ? employeeUserId : null,
        EmployeePayoff: 0,
        services: [
          {
            discountCurrency: 0,
            discountPercent: 0,
            price: 0,
            quantity: 0,
            service: null,
            upsaleEmployee: null,
            duration: 0,
            isExcludedFromLoyaltyDiscount: false,
          },
        ],
        products: [],
        subscriptions: [],
        certificates: [],
      };

      setEqualEmployeePayoff(true);

      const dataWithClient: RecordDetailsForm = {
        ...emptyData,
        Phone: {
          CountryId: getRecordPhoneCountryId(
            clientResponse?.Phone1CountryId,
            CountryId
          ),
          Phone: clientResponse?.Phone1 ?? '',
        },
        IsPhoneVerified: clientResponse?.IsPhoneVerified ?? false,
        NameFirst: clientResponse?.NameFirst ?? '',
        NameLast: clientResponse?.NameLast ?? '',
      };
      setSelectedEmployee({
        RecordId: 0,
        EmployeeId: employeeUserId,
      });
      onChangeServiceOptions(ClientServices as Array<ClientServiceInterface>);
      setBranchUsers(BranchUsers);
      setBranchEmployees(BranchEmployees);
      setWarehouses(Warehouses ?? []);
      setTags(Tags);
      // Клиент из карточки клиента или лида: его номер поиск не запускает.
      resetPhoneClientSearch();
      phoneClientRef.current = clientResponse
        ? createPhoneClient(
            initialClientId,
            clientResponse.Phone1,
            clientResponse.NameFirst,
            clientResponse.NameLast
          )
        : null;
      setFieldsValue(dataWithClient);

      await initUnsavedData(emptyData);

      dispatch(setIsLoading(false));
    },
    [
      onChangeServiceOptions,
      activeBranchCountry,
      prepareRecordCreationHandler,
      setFieldsValue,
      time,
      endTime,
      dispatch,
      date,
      showNotification,
      initUnsavedData,
      currentClientId,
      clientIdQueryParam,
      getClientByIdHandler,
      allEmployeeServicesHandler,
      resetPhoneClientSearch,
    ]
  );

  const toggleLock = useCallback(() => {
    const IsLocked = !formValues?.IsLocked;
    const updatedTags = (
      IsLocked
        ? [...(formValues?.Tags ?? []), IMPORTANT_EMPLOYEE_TAG_ID]
        : (formValues?.Tags ?? []).filter(
            (tag) => tag !== IMPORTANT_EMPLOYEE_TAG_ID
          )
    ).filter((tag) => tag !== null && tag !== undefined);

    setFieldsValue({ IsLocked, Tags: updatedTags });
  }, [setFieldsValue, formValues, IMPORTANT_EMPLOYEE_TAG_ID]);

  const initEditRecordForm = useCallback(
    async (recordId: number) => {
      dispatch(setIsLoading(true));

      const record = await getRecordByIdHandler(recordId);

      if (!record) {
        dispatch(setIsLoading(false));
        showNotification(
          'Непредвиденная ошибка при открытии записи: отсутствует запись',
          FRONT_WARNING
        );
        return;
      }

      const {
        EmployeeId,
        TimeS,
        TimeE,
        NameFirst,
        NameLast,
        PhoneCountryId,
        Phone,
        IsPhoneVerified,
        ClientId,
        RecordColor,
        Tags: RecordTags,
        Comment,
        Status,
        ClientServices: RecordServices,
        IsEmployeeLocked,
        EmployeePayoff,
        IsFullyPaid,
      } = record;

      // Refresh the slice record with the freshly fetched one so that
      // slice-derived locks (IsLoyaltyApplied, IsFullyPaid) recompute after an
      // in-modal payment when returning to the details tab without a page reload.
      dispatch(setRecord(record));
      savedSubscriptionsRef.current = null;
      dispatch(setIsFullyPaid(IsFullyPaid));

      const prepareRecordCreationResponse = await prepareRecordCreationHandler({
        employeeId: EmployeeId,
        dateTime: moment(TimeS).format('HH:mm'),
        recordId,
      });

      if (!prepareRecordCreationResponse) {
        dispatch(setIsLoading(false));
        showNotification(
          'Непредвиденная ошибка при открытии записи: отсутствует запись',
          FRONT_WARNING
        );
        return;
      }

      const allEmployeeServices = await allEmployeeServicesHandler(EmployeeId);

      setAllEmployeeServices(allEmployeeServices);

      const { BranchEmployees, Tags, ClientServices, BranchUsers, Warehouses } =
        prepareRecordCreationResponse;

      if (!employeeUserId) {
        const currentEmployee = BranchUsers.find(
          ({ UserId }) => UserId === EmployeeId
        );
        if (!currentEmployee) {
          showNotification(
            'Непредвиденная ошибка при открытии записи: не найден исполнитель',
            FRONT_WARNING
          );
          return;
        }

        dispatch(
          setEmployee({
            UserId: currentEmployee?.UserId,
            NameLast: currentEmployee?.NameLast || '',
            NameFirst: currentEmployee?.NameFirst || '',
            NameMiddle: currentEmployee?.NameMiddle || '',
            NameFull: '',
            Photo: '',
            PositionId: 0,
            Position: '',
            WorkSchedules: [],
            WorkBreaks: [],
            Records: [record],
            Id: currentEmployee?.UserId,
          })
        );
        const clientRecords: OtherRecordsInterface[] = [
          {
            Id: record.Id,
            TimeE: record.TimeE,
            TimeS: record.TimeS,
            EmployeeId: record.EmployeeId,
            Status: record.Status,
            NameFirst: currentEmployee?.NameFirst ?? '',
            NameLast: currentEmployee?.NameLast ?? '',
            NameMiddle: currentEmployee?.NameMiddle ?? '',
          },
          ...(record.OtherRecords || []),
        ];
        dispatch(setClientAllRecords(clientRecords));
      }

      const branchEmployee = BranchUsers?.find(
        ({ UserId }) => UserId === record?.EmployeeId
      );
      const clientRecords: OtherRecordsInterface[] = [
        {
          Id: record.Id,
          TimeE: record.TimeE,
          TimeS: record.TimeS,
          EmployeeId: record.EmployeeId,
          Status: record.Status,
          NameFirst: branchEmployee?.NameFirst ?? '',
          NameLast: branchEmployee?.NameLast ?? '',
          NameMiddle: branchEmployee?.NameMiddle ?? '',
        },
        ...(record.OtherRecords || []),
      ];
      dispatch(setClientAllRecords(clientRecords));

      setBranchUsers(BranchUsers);
      setBranchEmployees(BranchEmployees);
      setWarehouses(Warehouses ?? []);
      onChangeServiceOptions(ClientServices as Array<ClientServiceInterface>);
      setTags(Tags);
      const recordClientName = `${NameLast || ''} ${NameFirst}`.trim();

      onChangeCurrentClient(ClientId, recordClientName);
      dispatch(
        setSavedClient({
          clientId: ClientId,
          clientName: recordClientName || null,
        })
      );
      setSelectedEmployee({
        RecordId: recordId,
        EmployeeId: EmployeeId,
      });
      const DurationHour = moment(TimeE).diff(moment(TimeS), 'hours');
      const DurationMinute = moment(TimeE).diff(moment(TimeS), 'minutes') % 60;

      const services: Array<Service> =
        mapRecordServicesToFormServices(RecordServices);

      const emptyEmployeePayoff =
        EmployeePayoff === null || EmployeePayoff === undefined;

      const serviceEmployeePayoff = getSelectedServiceEmployeePayoff(
        services,
        allEmployeeServices
      );
      const data: RecordDetailsForm = {
        Phone: {
          CountryId: PhoneCountryId,
          Phone: Phone,
        },
        IsPhoneVerified: IsPhoneVerified,
        NameFirst,
        NameLast,
        Employee: EmployeeId,
        IsLocked: IsEmployeeLocked,
        Color: RecordColor ?? null,
        Tags: RecordTags.map(({ Id }) => Id),
        Comment: Comment || '',
        DurationHour,
        DurationMinute,
        Date: dayjs(TimeS),
        TimeStart: moment(TimeS).format('HH:mm'),
        TimeEnd: moment(TimeE).format('HH:mm'),
        Status: Status as ClientStatusEnum,
        services,
        products: mapRecordProductsToDraftItems(record.Products),
        subscriptions: mapRecordSubscriptionsToDraftItems(record.Subscriptions),
        // Сертификаты записи бэкенд пока не отдаёт: блок открывается пустым.
        certificates: [],
        EmployeePayoff,
      };

      setEqualEmployeePayoff(
        emptyEmployeePayoff || EmployeePayoff === serviceEmployeePayoff
      );

      // Номер записи — Phone1 её клиента (beauty-crm LogicRecords.cs:2523).
      resetPhoneClientSearch();
      phoneClientRef.current = ClientId
        ? createPhoneClient(ClientId, Phone, NameFirst, NameLast)
        : null;
      setFieldsValue(data);
      await initUnsavedData(data);
      dispatch(setIsLoading(false));
    },
    [
      dispatch,
      getRecordByIdHandler,
      prepareRecordCreationHandler,
      onChangeServiceOptions,
      onChangeCurrentClient,
      setFieldsValue,
      initUnsavedData,
      showNotification,
      getSelectedServiceEmployeePayoff,
      allEmployeeServicesHandler,
      employeeUserId,
      resetPhoneClientSearch,
    ]
  );

  const initForm = useCallback(() => {
    if (!isOpen) return;
    if (isInitialized) return;

    if (recordIdForOpen) {
      openRecordById(recordIdForOpen);
      return;
    }

    switch (mode) {
      case 'edit':
        if (!recordId) {
          showNotification(
            'Непредвиденная ошибка при открытии записи: отсутствует запись',
            FRONT_WARNING
          );
          break;
        }

        initEditRecordForm(recordId);
        break;
      case 'create':
        initCreateRecordForm(employeeUserId ?? 0);
        break;
      default:
        showNotification(
          'Непредвиденная ошибка при открытии записи',
          FRONT_WARNING
        );
        break;
    }
  }, [
    isOpen,
    employeeUserId,
    initCreateRecordForm,
    initEditRecordForm,
    mode,
    recordId,
    showNotification,
    recordIdForOpen,
    openRecordById,
    isInitialized,
  ]);

  const showPaymentButton = useMemo(
    () => mode === 'edit' && IsPayRecord,
    [mode, IsPayRecord]
  );

  // Поле «Поиск» есть в новой и в существующей записи: в существующей
  // записи им меняют клиента (issue/11575).
  const showClientSearch = mode === 'create' || mode === 'edit';

  // Строка «Абонементы» с saleId продана. Новая строка (saleId = 0) ещё не
  // продана: её продаст SetRecordSubscriptions после UpdateRecord, то есть
  // уже новому клиенту записи.
  const clientSearchLockReason = useMemo(
    () =>
      mode === 'edit' &&
      (savedData?.subscriptions ?? []).some(({ saleId }) => !!saleId)
        ? CLIENT_SEARCH_SOLD_SUBSCRIPTION_HINT
        : null,
    [mode, savedData]
  );

  // Филиал записи: в нём FindClientsByPhone ищет клиентов.
  const phoneSearchBranchId =
    (mode === 'edit' ? record?.BranchId : null) || recordActiveBranchId;

  // Номер в поле «Мобильный телефон» набран целиком (task/11629): компонент
  // Phone вызывает onFinish на каждое изменение длиной от MinDigits до
  // MaxDigits страны после ручного ввода. Номер выбранного клиента не ищем.
  // Найдены клиенты, не найдены или запрос упал — клиент снимается. Клиент
  // с пустым Phone1 остаётся: номер записи станет его номером, а найденных
  // клиентов меню покажет для выбора. Ответ на прежний номер не применяется.
  const onChangePhone = useCallback(
    async (phone?: string) => {
      if (!phone || !rightToViewContacts || clientSearchLockReason) return;

      const phoneDigits = getComparablePhone(phone);

      if (phoneClientRef.current?.phone === phoneDigits) {
        resetPhoneClientSearch();
        clearPhoneClientError();
        return;
      }

      const result = await searchPhoneClients({
        phone: phoneDigits,
        branchId: phoneSearchBranchId,
      });

      if (result === 'stale') return;

      if (!phoneClientRef.current?.isPhoneEmpty) {
        detachPhoneClient();
      }

      // «Сохранить» нажали до ответа: текст ошибки сменится на текст ответа.
      const phoneErrors = getFieldError('Phone');

      if (
        result === 'not-found' ||
        phoneErrors.includes(PHONE_CLIENT_LOADING_ERROR)
      ) {
        const resultError = PHONE_CLIENT_RESULT_ERRORS[result];

        setFields([
          { name: 'Phone', errors: resultError ? [resultError] : [] },
        ]);
      }
    },
    [
      rightToViewContacts,
      clientSearchLockReason,
      resetPhoneClientSearch,
      clearPhoneClientError,
      searchPhoneClients,
      phoneSearchBranchId,
      detachPhoneClient,
      getFieldError,
      setFields,
    ]
  );

  // Номер в поле стал другим без нового поиска: меню прежнего номера
  // скрыто (ux C3.2).
  const isPhoneSearchCurrent =
    getComparablePhone(formValues?.Phone?.Phone) === searchedPhone;
  const shownPhoneClients = useMemo(
    () => (isPhoneSearchCurrent ? phoneClients : []),
    [isPhoneSearchCurrent, phoneClients]
  );

  const totalDuration = useMemo(
    () => ({
      hours: formValues?.DurationHour,
      minutes: formValues?.DurationMinute,
    }),
    [formValues]
  );

  const getFormattedData = useCallback(
    (data: RecordDetailsForm): RecordDetailsForm => {
      const array = data?.services || [];
      const formattedServices = array
        ?.filter((item) => item !== null && item !== undefined)
        ?.filter(({ service }) => !!service);

      const shownEmployeePayoff =
        data && data?.EmployeePayoff !== null && data?.EmployeePayoff > 0
          ? data?.EmployeePayoff
          : null;

      const formattedProducts = (data?.products || [])
        .filter((item) => item.warehouseId && item.productId)
        .map(
          ({
            warehouseId,
            productId,
            productName,
            quantity,
            price,
            discountCurrency,
            sellerId,
            groupId,
            discounts,
            totalDiscount,
            paid,
            cost,
            isExcludedFromLoyaltyDiscount,
          }) => ({
            id: '',
            warehouseId,
            productId,
            productName: productName ?? null,
            quantity,
            price,
            discountCurrency,
            discountPercent: 0,
            sellerId: sellerId ?? null,
            groupId: groupId ?? null,
            discounts: discounts ?? [],
            totalDiscount: totalDiscount ?? 0,
            paid,
            cost,
            isExcludedFromLoyaltyDiscount,
          })
        );

      // Как у товаров: пустые строки (абонемент не выбран) несохранёнными
      // данными не считаются, ключ строки UI в сравнение не входит.
      const formattedSubscriptions = (data?.subscriptions || [])
        .filter(({ subscriptionId }) => subscriptionId)
        .map((item) => ({ ...item, id: '', discountPercent: 0 }));

      const formattedCertificates = (data?.certificates || [])
        .filter(({ certificateId }) => certificateId)
        .map((item) => ({ ...item, id: '', discountPercent: 0 }));

      const formattedData: RecordDetailsForm = {
        ...data,
        Phone: {
          ...data?.Phone,
          Phone: data?.Phone?.Phone?.replace(/\D/g, ''),
        },
        services: formattedServices,
        products: formattedProducts,
        subscriptions: formattedSubscriptions,
        certificates: formattedCertificates,
        EmployeePayoff: EditEmployeePayoff
          ? Number(shownEmployeePayoff)
          : Number(record?.EmployeePayoff) || null,
      };

      return formattedData;
    },
    [EditEmployeePayoff, record?.EmployeePayoff]
  );

  const checkUnsavedData = useCallback(() => {
    if (!isInitialized || !savedData || !formValues) return;

    const formattedSavedData = getFormattedData(savedData);
    const formattedUnsavedData = getFormattedData(formValues);

    let normalizedSavedData = formattedSavedData;
    let normalizedUnsavedData = formattedUnsavedData;

    if (
      EditEmployeePayoff &&
      equalEmployeePayoff &&
      record &&
      (record.EmployeePayoff === null || record.EmployeePayoff === undefined)
    ) {
      const servicesPayoff = getSelectedServiceEmployeePayoff(
        normalizedUnsavedData.services || [],
        allEmployeeServices
      );

      const savedEmployeePayoff = normalizedSavedData.EmployeePayoff ?? null;
      const unsavedEmployeePayoff =
        normalizedUnsavedData.EmployeePayoff ?? null;

      if (
        servicesPayoff > 0 &&
        unsavedEmployeePayoff === servicesPayoff &&
        (savedEmployeePayoff === null || savedEmployeePayoff === 0)
      ) {
        normalizedSavedData = {
          ...normalizedSavedData,
          EmployeePayoff: servicesPayoff,
        };
        normalizedUnsavedData = {
          ...normalizedUnsavedData,
          EmployeePayoff: servicesPayoff,
        };
      }
    }

    // Сторона без ключа ClientFieldValues уравнивается на загруженные
    // значения полей клиента: снапшоты init-путей этого ключа не содержат,
    // несохранёнными должны считаться только реальные правки пользователя.
    if (clientFieldBaseline) {
      if (normalizedSavedData.ClientFieldValues === undefined) {
        normalizedSavedData = {
          ...normalizedSavedData,
          ClientFieldValues: clientFieldBaseline,
        };
      }
      if (normalizedUnsavedData.ClientFieldValues === undefined) {
        normalizedUnsavedData = {
          ...normalizedUnsavedData,
          ClientFieldValues: clientFieldBaseline,
        };
      }
    }

    dispatch(setUnsavedRecordForm(normalizedUnsavedData));

    // Клиент существующей записи меняется полем «Поиск» (issue/11575).
    // Клиент с теми же именем и телефоном не меняет полей формы, поэтому
    // смену клиента сравниваем с клиентом записи при загрузке или последнем
    // сохранении. record.ClientId для этого не годится: setClientId меняет и
    // его.
    const isRecordClientChanged =
      mode === 'edit' &&
      !!savedClient &&
      (currentClientId ?? 0) !== (savedClient.clientId ?? 0);
    const hasChanges =
      isRecordClientChanged ||
      !isEqual(normalizedSavedData, normalizedUnsavedData);
    dispatch(setHasChanges(hasChanges));
  }, [
    isInitialized,
    savedData,
    formValues,
    mode,
    currentClientId,
    savedClient,
    EditEmployeePayoff,
    equalEmployeePayoff,
    record,
    allEmployeeServices,
    clientFieldBaseline,
    dispatch,
    getFormattedData,
    getSelectedServiceEmployeePayoff,
  ]);

  const handleConfirm = useCallback(() => {
    onConfirmRef.current = submit;
  }, [onConfirmRef, submit]);

  const onDeleteRecord = useCallback(async () => {
    if (isDeleteRecordLoading) return;

    if (!record?.Id) {
      showNotification(
        'Непредвиденная ошибка при открытии записи: отсутствует запись',
        FRONT_WARNING
      );
      return;
    }

    try {
      await deleteRecordFromJournal({
        ActiveBranchId: recordActiveBranchId,
        recordId: record?.Id,
      }).unwrap();
      const today = moment().format('YYYY-MM-DD');

      if (today !== date && date) {
        const newParams = new URLSearchParams({ date });
        setSearchParams(newParams, { replace: true });
      } else {
        const newParams = new URLSearchParams(searchParams);
        newParams.delete('date');
        setSearchParams(newParams, { replace: true });
      }
      afterSave(date || today);

      closeDeleteRecordModal();
      showNotification('Запись успешно удалена', 'success');
      dispatch(closeRecordModal());
    } catch (error) {
      showNotification(error);
      setErrorModalMessage(handleCatchError(error));
    }
  }, [
    isDeleteRecordLoading,
    recordActiveBranchId,
    record?.Id,
    showNotification,
    dispatch,
    deleteRecordFromJournal,
    date,
    setSearchParams,
    afterSave,
    searchParams,
  ]);

  const onSelectEmployee = useCallback(
    async ({ RecordId, EmployeeId }: SelectedEmployee) => {
      if (hasChanges) {
        const action = await confirmUnsavedData();
        switch (action) {
          case 'confirm':
            await saveBeforeEmployeeChange({
              validate: () => validateFields(),
              save: () => onFinish(formValues, RecordId),
              selectEmployee: () =>
                setSelectedEmployee({ RecordId, EmployeeId }),
            });

            break;
          case 'discard':
            setSelectedEmployee({ RecordId, EmployeeId });
            openRecordById(RecordId);
            break;
          case 'cancel':
            break;
          default:
            break;
        }
      } else {
        setSelectedEmployee({ RecordId, EmployeeId });
        openRecordById(RecordId);
      }
    },
    [
      openRecordById,
      hasChanges,
      formValues,
      onFinish,
      confirmUnsavedData,
      validateFields,
    ]
  );

  const showEmployeeTabs = useMemo(
    () => mode === 'edit' && clientAllRecords?.length > 1,
    [clientAllRecords, mode]
  );

  const mainEmployeeSelected = form.getFieldValue('Employee');

  const mainEmployeeOptions = useMemo(() => {
    const users =
      selectedServicesIds.length > 0 ? serviceEmployees : branchEmployees;

    const options: TEmployeeOptions[] =
      (users || [])?.map(({ NameLast, NameFirst, UserId }) => ({
        label: `${NameLast} ${NameFirst}`.trim(),
        value: UserId ?? 0,
      })) ?? [];

    const filteredOptions = options.filter(
      ({ value }) =>
        !additionalEmployees.filter((v) => v !== null)?.includes(value)
    );

    return filteredOptions;
  }, [
    selectedServicesIds,
    serviceEmployees,
    branchEmployees,
    additionalEmployees,
  ]);

  const additinalEmployeeOptions = useMemo(() => {
    const options: TEmployeeOptions[] = (branchEmployees || [])
      ?.map(({ NameLast, NameFirst, UserId }) => ({
        label: `${NameLast} ${NameFirst}`.trim(),
        value: UserId ?? 0,
      }))
      .filter(({ value }) => mainEmployeeSelected !== value);

    return options;
  }, [branchEmployees, mainEmployeeSelected]);

  const handleDuplicateClick = useCallback(
    () => setAdditionalEmployees([null]),
    []
  );

  const handleAdditionalEmployeeChange = useCallback(
    (index: number, value: number) => {
      const updated = [...additionalEmployees];
      updated[index] = value;
      setAdditionalEmployees(updated);
    },
    [additionalEmployees]
  );

  const handleRemoveAdditionalEmployee = useCallback(
    (index: number) => {
      const updated = additionalEmployees.filter((_, i) => i !== index);
      setAdditionalEmployees(updated);
    },
    [additionalEmployees]
  );

  const handleAddAdditionalEmployee = useCallback(() => {
    setAdditionalEmployees([...additionalEmployees, null]);
  }, [additionalEmployees]);

  const showDoubleRecordButton = useMemo(
    () =>
      additionalEmployees.length === 0 && additinalEmployeeOptions.length > 0,
    [additinalEmployeeOptions.length, additionalEmployees.length]
  );

  const showDoubleRecordSelects = useMemo(
    () => additionalEmployees.length > 0,
    [additionalEmployees.length]
  );

  const isFormDisabled = useMemo(() => {
    // `isFullyPaid` is the live in-session flag set by the payment tab, so the
    // form locks right after payment without reopening the modal, while
    // `record.IsFullyPaid` covers records opened as already paid.
    return (
      mode === 'edit' &&
      !IsEditPaidRecord &&
      (record?.IsFullyPaid || isFullyPaid)
    );
  }, [mode, record, IsEditPaidRecord, isFullyPaid]);

  const isLoyaltyApplied = useMemo(() => {
    return record?.IsLoyaltyApplied;
  }, [record?.IsLoyaltyApplied]);

  const employeePayoffHandler = useCallback(() => {
    if (!isOpen || !isInitialized || !equalEmployeePayoff) return;

    const serviceEmployeePayoff = getSelectedServiceEmployeePayoff(
      formValues?.services,
      allEmployeeServices
    );

    if (serviceEmployeePayoff > 0) {
      setFieldValue('EmployeePayoff', serviceEmployeePayoff);
    }
  }, [
    isOpen,
    isInitialized,
    equalEmployeePayoff,
    getSelectedServiceEmployeePayoff,
    setFieldValue,
    formValues?.services,
    allEmployeeServices,
  ]);

  return {
    form,
    formValues,
    onSelectStatus,
    checkDisabledStatus,
    onFinish,
    showPaymentButton,
    showClientSearch,
    clientSearchLockReason,
    tagOptions,
    initForm,
    resetForm,
    toggleLock,
    importantEmployeeHandler,
    serviceOptions,
    clientSearchTerm,
    onChangeClientSearchTerm,
    clientSearchIsLoading,
    clientSearchIsSuccess,
    isClientSearchTooShort,
    currentClientId,
    currentClientName,
    onSelectClient,
    clientSearchResults,
    onChangePhone,
    phoneClients: shownPhoneClients,
    isPhoneClientMenuOpen: isPhoneClientMenuOpen && isPhoneSearchCurrent,
    isPhoneClientSearchLoading,
    isPhoneClientSearchSuccess,
    phoneClientRules,
    onPhoneInputFocus,
    onPhoneInputBlur,
    onPhoneCountryListOpenChange,
    onSelectPhoneClient,
    restoreSavedClientFields,
    handleConfirm,
    isLoading,
    isOpen,
    totalDuration,
    formRef,
    onConfirm: submit,
    checkUnsavedData,
    isInitialized,
    hasChanges,
    showDeleteButton,
    isOpenDeleteRecordModal,
    closeDeleteRecordModal,
    openDeleteRecordModal,
    onDeleteRecord,
    isDeleteRecordLoading,
    isOpenErrorModal,
    closeErrorModal,
    errorModalMessage,
    isCommentModal,
    setIsCommentModal,
    clientAllRecords,
    selectedEmployee,
    onSelectEmployee,
    showEmployeeTabs,
    getEmployeesByServicesHandler,
    branchUserOptions,
    warehouses,
    recordActiveBranchId,
    mainEmployeeOptions,
    additinalEmployeeOptions,
    handleDuplicateClick,
    handleAdditionalEmployeeChange,
    handleRemoveAdditionalEmployee,
    handleAddAdditionalEmployee,
    showDoubleRecordButton,
    showDoubleRecordSelects,
    additionalEmployees,
    mainEmployeeSelected,
    containerRef,
    checkRecordTimesHandler,
    setPhoneValidation,
    phoneValidation,
    isSaveRecordButtonDisabled,
    rightToViewContacts,
    EditEmployeePayoff,
    isFormDisabled,
    employeePayoffHandler,
    onSelectEmployeeForService,
    isFetchingGetClientServicesByEmployeeId,
    isLoadingGetClientServicesByEmployeeId,
    tags,
    setTags,
    getAllTagsHandler,
    allTags,
    closeRecordDetails,
    isLoyaltyApplied,
    recordId,
    recordType,
    savedServices,
  };
};
